'use client';

import { useEffect, useState } from 'react';

import { ACCOUNT_COPY } from '@eliadmin/core/src/components/dlz/account/account-copy';
import { AccountHeading } from '@eliadmin/core/src/components/dlz/account/account-heading';
import {
  CartResultNotice,
  cartResultSummary,
  useAddAllToCart,
} from '@eliadmin/core/src/components/dlz/account/favorites/add-all-to-cart';
import {
  countDiscounted,
  priceAlertText,
  productCount,
  useFavoriteProducts,
  type FavoritesSort as Sort,
} from '@eliadmin/core/src/components/dlz/account/favorites/favorites';
import { FAVORITES_COPY } from '@eliadmin/core/src/components/dlz/account/favorites/favorites-copy';
import {
  FavoritesGrid,
  FavoritesGridSkeleton,
} from '@eliadmin/core/src/components/dlz/account/favorites/favorites-grid';
import { FavoritesNotice } from '@eliadmin/core/src/components/dlz/account/favorites/favorites-notice';
import { FavoritesSort } from '@eliadmin/core/src/components/dlz/account/favorites/favorites-sort';
import {
  FavoritesStateCard,
  STATE_CARD_BUTTON,
} from '@eliadmin/core/src/components/dlz/account/favorites/favorites-state-card';
import { RemoveAllFavorites } from '@eliadmin/core/src/components/dlz/account/favorites/remove-all-favorites';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

/**
 * „Obľúbené produkty“ of a signed-in customer (Figma desktop 60325:20610, mobile 60459:92480) in
 * the content column of the account shell: heading with the count, toolbar (60325:21101: „Pridať
 * všetko do košíka“ and „Zoradiť“), the price alert (60325:21144, only with discounted
 * favourites), the card grid and „Zobraziť ďalšie“. Everything that depends on the column width is a
 * container query: under 31rem (a phone-wide column) the button takes the full width with
 * „Odstrániť všetky obľúbené“ under it and the sort moves under the alert (`display: contents` +
 * `order` – one sort menu in the DOM).
 */
export function FavoritesView() {
  const { t } = useTranslations();
  const [sort, setSort] = useState<Sort>('recent');
  const favorites = useFavoriteProducts(sort);
  const addAll = useAddAllToCart();
  const products = favorites.data;
  const discounted = products ? countDiscounted(products) : 0;
  // The empty card takes the focus only when it replaced the list during this visit.
  const [hadFavorites, setHadFavorites] = useState(false);
  useEffect(() => {
    if (products?.length) setHadFavorites(true);
  }, [products?.length]);

  return (
    <div className="flex flex-col gap-6 @container">
      <AccountHeading
        title={ACCOUNT_COPY.pages.favorites.label}
        description={
          products ? (
            `${productCount(products.length)} · ${ACCOUNT_COPY.pages.favorites.description}`
          ) : favorites.isError ? undefined : (
            // The count line keeps its place while the list loads.
            <span
              aria-hidden
              className="block h-[22px] w-2/3 animate-pulse rounded-md bg-[#F5F6F7]"
            />
          )
        }
      />

      {!products ? (
        favorites.isError ? (
          <FavoritesStateCard
            icon="warning"
            title={FAVORITES_COPY.loadFailed.title}
            text={FAVORITES_COPY.loadFailed.text}
            actions={
              <Button
                variant="secondary"
                className={STATE_CARD_BUTTON}
                onClick={() => void favorites.refetch()}
              >
                {t('text595')}
              </Button>
            }
          />
        ) : (
          <FavoritesGridSkeleton />
        )
      ) : !products.length ? (
        <FavoritesStateCard
          icon="heart"
          title={t('text1381')}
          focusTitle={hadFavorites}
          text={FAVORITES_COPY.empty.text}
          actions={
            <Button asChild className={STATE_CARD_BUTTON}>
              <Link href={ROUTES.CATEGORIES()} prefetch={false}>
                {FAVORITES_COPY.empty.browse}
              </Link>
            </Button>
          }
        />
      ) : (
        <>
          <div className="contents @[31rem]:flex @[31rem]:items-center @[31rem]:justify-between @[31rem]:gap-4">
            <div className="flex flex-col items-center gap-6">
              <Button
                iconLeft="cart"
                isLoading={addAll.isPending}
                onClick={() => addAll.mutate(products)}
                className="h-12 w-full @[31rem]:h-[52px] @[31rem]:w-auto"
              >
                {FAVORITES_COPY.addAll}
              </Button>
              <RemoveAllFavorites className="@[31rem]:hidden" />
              <p role="status" className="sr-only">
                {addAll.data ? cartResultSummary(addAll.data) : null}
              </p>
            </div>
            <FavoritesSort
              value={sort}
              onChange={setSort}
              className="order-1 self-start @[31rem]:order-none @[31rem]:self-auto"
            />
          </div>
          {addAll.data ? (
            <CartResultNotice result={addAll.data} onDismiss={() => addAll.reset()} />
          ) : null}
          {discounted ? (
            <FavoritesNotice icon="percent-duotone">
              <p>{priceAlertText(discounted)}</p>
            </FavoritesNotice>
          ) : null}
          <div className="order-2" aria-busy={favorites.isFetching}>
            <FavoritesGrid products={products} />
          </div>
        </>
      )}
    </div>
  );
}
